import { useEffect, useRef } from 'react'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query'
import { ThemeProvider } from 'next-themes'
import { Loader2, WifiOff } from 'lucide-react'
import { queryClient } from './lib/api/queryClient'
import { useAuth } from './lib/auth/useAuth'
import { routeTree } from './routeTree.gen'
import { Toaster } from '@/components/ui/sonner'
import { Button } from '@/components/ui/button'
import { ErrorScreen } from '@/components/error-screen'
import { PageMain } from '@/components/page-main'

const router = createRouter({
  routeTree,
  context: undefined!,
  defaultPreload: 'intent',
  defaultErrorComponent: ({ error, reset }) => (
    <ErrorScreen error={error} reset={reset} />
  ),
  defaultNotFoundComponent: () => (
    <ErrorScreen
      error="The page you were looking for doesn't exist."
      title="Page not found"
      eyebrow="404"
    />
  ),
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

function InnerApp() {
  const auth = useAuth()
  const prevAuth = useRef(auth.isAuthenticated)

  // Router context is captured at navigation time; `beforeLoad` won't see a
  // post-mount auth flip unless we invalidate. This lets the existing
  // `beforeLoad` guards on _app/login be the single source of truth for the
  // redirect, instead of a parallel useEffect on each route.
  useEffect(() => {
    if (prevAuth.current !== auth.isAuthenticated) {
      prevAuth.current = auth.isAuthenticated
      router.invalidate()
    }
  }, [auth.isAuthenticated])

  // Don't render the router until we know the auth state. Both branches
  // below render full-bleed screens so the user never sees an unstyled
  // flash, even on a cold boot before the router takes over.
  if (auth.isLoading) {
    return <BootScreen state="loading" />
  }

  if (auth.error) {
    return <BootScreen state="error" />
  }

  return <RouterProvider router={router} context={{ auth, queryClient }} />
}

export function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
      <QueryClientProvider client={queryClient}>
        <InnerApp />
        <Toaster />
      </QueryClientProvider>
    </ThemeProvider>
  )
}

export default App

function BootScreen({ state }: { state: 'loading' | 'error' }) {
  const qc = useQueryClient()

  return (
    <PageMain className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background">
      <div className="absolute inset-0 hawk-grid pointer-events-none" aria-hidden />
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 50% 30%, rgba(255, 77, 0, 0.08), transparent 70%)',
        }}
      />

      <div className="absolute top-6 left-6 font-mono text-[10px] uppercase tracking-[0.32em] text-muted-foreground flex items-center gap-4">
        <span className="text-foreground/80">Hawk</span>
        <span>{state === 'loading' ? 'Booting' : 'Disconnected'}</span>
      </div>

      <div className="relative w-full max-w-sm px-6 animate-hawk-rise text-center">
        <BrandMark />

        {state === 'loading' ? (
          <div
            role="status"
            aria-live="polite"
            className="mt-10 flex flex-col items-center gap-4"
          >
            <Loader2
              className="size-5 text-primary animate-spin"
              strokeWidth={1.5}
              aria-hidden
            />
            <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-muted-foreground">
              Verifying session
            </span>
          </div>
        ) : (
          <div role="alert" className="mt-10 space-y-5">
            <div className="flex items-center justify-center gap-2 text-destructive">
              <WifiOff className="size-4" strokeWidth={1.5} aria-hidden />
              <span className="font-mono text-[10px] uppercase tracking-[0.32em]">
                Unable to reach Hawk
              </span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The backend isn't responding. Check that the Hawk server is
              running and that your network can reach it.
            </p>
            <Button
              onClick={() =>
                qc.invalidateQueries({ queryKey: ['auth', 'session'] })
              }
              className="w-full h-10"
            >
              Retry connection
            </Button>
          </div>
        )}
      </div>
    </PageMain>
  )
}

function BrandMark() {
  return (
    <div className="inline-flex items-baseline" role="img" aria-label="hawk">
      <span
        aria-hidden
        className="text-[64px] leading-[0.85] font-medium tracking-[-0.04em]"
      >
        hawk
      </span>
      <span
        aria-hidden
        className="text-[64px] leading-[0.85] font-medium text-primary"
      >
        .
      </span>
    </div>
  )
}
